import { hydrateSceneryAirport, resolveCoords } from "./airports";
import { haversineNm } from "./geo";
import { LAST_KNOWN_STORE_MIN_MS, LAST_KNOWN_STORE_MIN_NM } from "./mapMemory";
import type { SceneryAirport } from "./types";

export type PositionSource = "simconnect" | "sayintentions" | "vatsim" | "last_known";

export type PositionSample = {
  lat: number;
  lon: number;
  heading: number | null;
  altitudeFt: number | null;
  altitudeAgl: number | null;
  airspeed: number | null;
  groundSpeed: number | null;
  verticalSpeedFpm: number | null;
  onGround: boolean | null;
  atUtc: string;
  /** Alias some HTTP stubs still send. Prefer `groundSpeed`. */
  groundSpeedKt?: number | null;
  /** Transponder / Mode A. Decimal 1200 or BCD16 (0x1200). */
  squawk?: number | null;
  /** True ground track when the sample has it. Else use `heading`. */
  trackDeg?: number | null;
  /** Optional live fuel — only when the sample actually carries it. */
  fuelTotalGal?: number | null;
  fuelUsedGal?: number | null;
  fuelStartGal?: number | null;
  /** Reliable SimConnect extras — fuel qty / combustion / N1 / APU. Never oil/hyd. */
  fuelQtyLb?: number | null;
  fuelQtyGal?: number | null;
  eng1Combustion?: boolean | null;
  eng2Combustion?: boolean | null;
  eng1N1?: number | null;
  eng2N1?: number | null;
  apuPctRpm?: number | null;
  eng3Combustion?: boolean | null;
  eng4Combustion?: boolean | null;
  eng1FlowPph?: number | null;
  eng2FlowPph?: number | null;
  eng3FlowPph?: number | null;
  eng4FlowPph?: number | null;
  unlimitedFuel?: boolean | null;
  fuelDumpActive?: boolean | null;
  tankCenterLb?: number | null;
  tankLeftMainLb?: number | null;
  tankRightMainLb?: number | null;
  tankLeftAuxLb?: number | null;
  tankRightAuxLb?: number | null;
  /** Gear down. Absent when the sample did not report the handle or a position. */
  gearDown?: boolean | null;
  gearHandleDown?: boolean | null;
  /** Nose/main gear percent. Above 5 counts as down. */
  gearPositionPct?: number | null;
  /** AIRSPEED TRUE (kt) and AMBIENT TEMPERATURE (°C) when the sim publishes them. */
  trueAirspeedKt?: number | null;
  outsideAirTempC?: number | null;
};

export type FusedPosition = PositionSample & {
  source: PositionSource;
  stale: boolean;
};

export type SimConnectSettings = {
  /** Poll native SimConnect (Windows .exe) then optional HTTP when true. */
  enabled: boolean;
  /** Optional HTTP JSON fallback. Empty still tries DEFAULT_SIMCONNECT_URL after native. Never a secret. */
  endpoint: string;
};

export type SimConnectTransport = "native" | "http";

export const DEFAULT_SIMCONNECT_URL = "http://127.0.0.1:8383/simconnect";

export const DEFAULT_SIMCONNECT: SimConnectSettings = {
  enabled: true,
  endpoint: "",
};

export const SIMCONNECT_SETTINGS_LABEL = "SimConnect position (MSFS on this PC)";

export const SIMCONNECT_SETTINGS_HINT =
  "Reads live aircraft lat/lon from Microsoft Flight Simulator on this computer. MSFS 2024 must be running locally on the same Windows PC as the desk. The Northstar .exe talks to SimConnect in-process — no tracker API key and no third-party HTTP bridge required. SayIntentions still supplies airports / callsign. Optional URL below is only a fallback if you run a custom JSON bridge. Not a secret.";

export const SIMCONNECT_HTTP_HINT =
  "Optional. Leave empty unless you run a custom JSON bridge. Empty still falls back to localhost:8383 after the built-in client. Expected JSON: { lat, lon, heading?, altitude? }.";

/** Fresh enough to be primary. Older samples fall through to last-known. */
export const POSITION_FRESH_MS = 45_000;

export function hydrateSimConnectSettings(raw?: Partial<SimConnectSettings> | null): SimConnectSettings {
  return {
    enabled: raw?.enabled !== false,
    endpoint: typeof raw?.endpoint === "string" ? raw.endpoint.trim() : "",
  };
}

export function simConnectUrl(settings: SimConnectSettings = DEFAULT_SIMCONNECT): string {
  return settings.endpoint.trim() || DEFAULT_SIMCONNECT_URL;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asBool(value: unknown): boolean | null {
  if (value === true || value === 1 || value === "1" || value === "true") return true;
  if (value === false || value === 0 || value === "0" || value === "false") return false;
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function pickNum(row: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const n = asNumber(row[key]);
    if (n != null) return n;
  }
  return null;
}

/**
 * Handle down or a position above 5% is gear down.
 * A published up handle, or a position at or under 5% with no handle, is gear up.
 * An explicit boolean is used only when the sim did not publish either var.
 */
export function resolveGearDown(
  explicit: boolean | null,
  handle: boolean | null,
  pct: number | null,
): boolean | null {
  if (handle === true || (pct != null && pct > 5)) return true;
  if (handle === false && (pct == null || pct <= 5)) return false;
  if (pct != null && pct <= 5 && handle == null) return false;
  if (explicit === true || explicit === false) return explicit;
  return null;
}

export function parsePositionSample(raw: unknown, now = new Date()): PositionSample | null {
  const root = asRecord(raw);
  if (!root) return null;
  const body = asRecord(root.position) ?? asRecord(root.simconnect) ?? asRecord(root.data) ?? root;
  const lat = pickNum(body, ["lat", "latitude", "Latitude"]);
  const lon = pickNum(body, ["lon", "lng", "longitude", "Longitude"]);
  if (lat == null || lon == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  const atRaw =
    typeof body.atUtc === "string"
      ? body.atUtc
      : typeof body.at_utc === "string"
        ? body.at_utc
        : typeof body.timestamp === "string"
          ? body.timestamp
          : "";
  const at = atRaw && !Number.isNaN(new Date(atRaw).getTime()) ? new Date(atRaw).toISOString() : now.toISOString();
  return {
    lat,
    lon,
    heading: pickNum(body, ["heading", "hdg", "Heading"]),
    altitudeFt: pickNum(body, ["altitudeFt", "altitude_ft", "altitude", "alt", "indicatedAltitude", "Altitude"]),
    altitudeAgl: pickNum(body, ["altitudeAgl", "altitude_agl", "agl", "AltitudeAGL"]),
    airspeed: pickNum(body, ["airspeed", "ias", "indicatedAirspeed", "Airspeed"]),
    groundSpeed: pickNum(body, ["groundSpeed", "ground_speed", "gs", "GroundVelocity", "groundSpeedKt"]),
    verticalSpeedFpm: pickNum(body, [
      "verticalSpeedFpm",
      "vertical_speed_fpm",
      "verticalSpeed",
      "vs",
      "vsFpm",
      "VerticalSpeed",
    ]),
    onGround: asBool(body.onGround ?? body.on_ground ?? body.OnGround),
    atUtc: at,
    groundSpeedKt: pickNum(body, ["groundSpeedKt", "groundSpeed", "ground_speed", "gs", "GroundSpeed"]),
    squawk: pickNum(body, ["squawk", "transponder", "xpdr", "transponderCode"]),
    trackDeg: pickNum(body, ["trackDeg", "track_deg", "track", "trk", "groundTrack", "gpsTrack"]),
    fuelTotalGal: pickNum(body, [
      "fuelTotalGal",
      "fuel_total_gal",
      "fuelQuantity",
      "fuel_quantity",
      "totalFuelGal",
    ]),
    fuelUsedGal: pickNum(body, ["fuelUsedGal", "fuel_used_gal", "fuelUsed"]),
    fuelStartGal: pickNum(body, ["fuelStartGal", "fuel_start_gal"]),
    fuelQtyLb: pickNum(body, ["fuelQtyLb", "fuel_qty_lb", "fuelTotalQuantityWeight", "fuel_total_quantity_weight"]),
    fuelQtyGal: pickNum(body, ["fuelQtyGal", "fuel_qty_gal", "fuelTotalQuantity", "fuel_total_quantity"]),
    eng1Combustion: asBool(body.eng1Combustion ?? body.eng1_combustion ?? body.generalEngCombustion1),
    eng2Combustion: asBool(body.eng2Combustion ?? body.eng2_combustion ?? body.generalEngCombustion2),
    eng1N1: pickNum(body, ["eng1N1", "eng1_n1", "turbEngN1_1"]),
    eng2N1: pickNum(body, ["eng2N1", "eng2_n1", "turbEngN1_2"]),
    apuPctRpm: pickNum(body, ["apuPctRpm", "apu_pct_rpm", "apuRpm"]),
    eng3Combustion: asBool(body.eng3Combustion ?? body.eng3_combustion),
    eng4Combustion: asBool(body.eng4Combustion ?? body.eng4_combustion),
    eng1FlowPph: pickNum(body, ["eng1FlowPph", "eng1_flow_pph"]),
    eng2FlowPph: pickNum(body, ["eng2FlowPph", "eng2_flow_pph"]),
    eng3FlowPph: pickNum(body, ["eng3FlowPph", "eng3_flow_pph"]),
    eng4FlowPph: pickNum(body, ["eng4FlowPph", "eng4_flow_pph"]),
    unlimitedFuel: asBool(body.unlimitedFuel ?? body.unlimited_fuel),
    fuelDumpActive: asBool(body.fuelDumpActive ?? body.fuel_dump_active),
    tankCenterLb: pickNum(body, ["tankCenterLb", "tank_center_lb"]),
    tankLeftMainLb: pickNum(body, ["tankLeftMainLb", "tank_left_main_lb"]),
    tankRightMainLb: pickNum(body, ["tankRightMainLb", "tank_right_main_lb"]),
    tankLeftAuxLb: pickNum(body, ["tankLeftAuxLb", "tank_left_aux_lb"]),
    tankRightAuxLb: pickNum(body, ["tankRightAuxLb", "tank_right_aux_lb"]),
    gearDown: resolveGearDown(
      asBool(body.gearDown ?? body.gear_down),
      asBool(body.gearHandleDown ?? body.gear_handle_down ?? body.gear_handle_position),
      pickNum(body, ["gearPositionPct", "gear_position_pct", "gearPosition"]),
    ),
    trueAirspeedKt: pickNum(body, ["trueAirspeedKt", "true_airspeed_kt", "trueAirspeed", "tas"]),
    outsideAirTempC: pickNum(body, ["outsideAirTempC", "outside_air_temp_c", "ambientTemperatureC", "oatC", "oat"]),
  };
}

export function sampleAgeMs(sample: Pick<PositionSample, "atUtc"> | null | undefined, now = new Date()): number {
  if (!sample?.atUtc) return Number.POSITIVE_INFINITY;
  const t = new Date(sample.atUtc).getTime();
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return Math.max(0, now.getTime() - t);
}

export function isFreshSample(sample: Pick<PositionSample, "atUtc"> | null | undefined, now = new Date()): boolean {
  return sampleAgeMs(sample, now) <= POSITION_FRESH_MS;
}

export function sampleFromSi(
  pos: {
    lat: number;
    lon: number;
    heading?: number | null;
    altitude?: number | null;
    altitudeAgl?: number | null;
    airspeed?: number | null;
    groundSpeed?: number | null;
    verticalSpeedFpm?: number | null;
    squawk?: number | null;
    trackDeg?: number | null;
  } | null,
  now = new Date(),
): PositionSample | null {
  if (!pos || !Number.isFinite(pos.lat) || !Number.isFinite(pos.lon)) return null;
  return {
    lat: pos.lat,
    lon: pos.lon,
    heading: pos.heading ?? null,
    altitudeFt: pos.altitude ?? null,
    altitudeAgl: pos.altitudeAgl ?? null,
    airspeed: pos.airspeed ?? null,
    groundSpeed: pos.groundSpeed ?? null,
    verticalSpeedFpm: pos.verticalSpeedFpm ?? null,
    onGround: null,
    atUtc: now.toISOString(),
    squawk: pos.squawk ?? null,
    trackDeg: pos.trackDeg ?? null,
  };
}

/** Display-only gaps. Never copy on-ground / AGL / VS — those drive phase. */
export function fillOpsGaps(primary: FusedPosition, extra: PositionSample): FusedPosition {
  return {
    ...primary,
    heading: primary.heading ?? extra.heading,
    altitudeFt: primary.altitudeFt ?? extra.altitudeFt,
    airspeed: primary.airspeed ?? extra.airspeed,
    groundSpeed: primary.groundSpeed ?? extra.groundSpeed ?? extra.groundSpeedKt ?? null,
    squawk: primary.squawk ?? extra.squawk ?? null,
    trackDeg: primary.trackDeg ?? extra.trackDeg ?? null,
  };
}

/**
 * SimConnect is primary when a fresh sample exists.
 * SI is used for live lat/lon when SimConnect is down (Wx/ops stay on the SI session).
 * VATSIM session is a fallback only when SI is not in play.
 * Last-known is used when the sim is closed.
 */
export function fusePosition(input: {
  simconnect?: PositionSample | null;
  sayintentions?: PositionSample | null;
  vatsim?: PositionSample | null;
  lastKnown?: FusedPosition | null;
  now?: Date;
}): FusedPosition | null {
  const now = input.now ?? new Date();
  const siInPlay = Boolean(input.sayintentions);
  let primary: FusedPosition | null = null;
  if (input.simconnect && isFreshSample(input.simconnect, now)) {
    primary = { ...input.simconnect, source: "simconnect", stale: false };
  } else if (input.sayintentions && isFreshSample(input.sayintentions, now)) {
    primary = { ...input.sayintentions, source: "sayintentions", stale: false };
  } else if (!siInPlay && input.vatsim && isFreshSample(input.vatsim, now)) {
    primary = { ...input.vatsim, source: "vatsim", stale: false };
  } else if (input.simconnect) {
    primary = { ...input.simconnect, source: "simconnect", stale: true };
  } else if (input.sayintentions) {
    primary = { ...input.sayintentions, source: "sayintentions", stale: true };
  } else if (!siInPlay && input.vatsim) {
    primary = { ...input.vatsim, source: "vatsim", stale: true };
  } else if (input.lastKnown && Number.isFinite(input.lastKnown.lat) && Number.isFinite(input.lastKnown.lon)) {
    primary = { ...input.lastKnown, source: "last_known", stale: true };
  }
  if (primary && !primary.stale) {
    const extra =
      primary.source === "simconnect" && input.sayintentions && isFreshSample(input.sayintentions, now)
        ? input.sayintentions
        : primary.source === "simconnect" && !siInPlay && input.vatsim && isFreshSample(input.vatsim, now)
          ? input.vatsim
          : primary.source === "sayintentions" && input.simconnect && isFreshSample(input.simconnect, now)
            ? input.simconnect
            : primary.source === "vatsim" && input.simconnect && isFreshSample(input.simconnect, now)
              ? input.simconnect
              : null;
    if (extra) primary = fillOpsGaps(primary, extra);
  }
  return primary;
}

export function hydrateFusedPosition(raw?: Partial<FusedPosition> | null): FusedPosition | null {
  if (!raw || typeof raw.lat !== "number" || typeof raw.lon !== "number") return null;
  if (!Number.isFinite(raw.lat) || !Number.isFinite(raw.lon)) return null;
  const source: PositionSource =
    raw.source === "simconnect" || raw.source === "sayintentions" || raw.source === "vatsim"
      ? raw.source
      : "last_known";
  return {
    lat: raw.lat,
    lon: raw.lon,
    heading: typeof raw.heading === "number" ? raw.heading : null,
    altitudeFt: typeof raw.altitudeFt === "number" ? raw.altitudeFt : null,
    altitudeAgl: typeof raw.altitudeAgl === "number" ? raw.altitudeAgl : null,
    airspeed: typeof raw.airspeed === "number" ? raw.airspeed : null,
    groundSpeed: typeof raw.groundSpeed === "number" ? raw.groundSpeed : typeof raw.groundSpeedKt === "number" ? raw.groundSpeedKt : null,
    verticalSpeedFpm: typeof raw.verticalSpeedFpm === "number" ? raw.verticalSpeedFpm : null,
    onGround: typeof raw.onGround === "boolean" ? raw.onGround : null,
    atUtc: typeof raw.atUtc === "string" ? raw.atUtc : new Date().toISOString(),
    groundSpeedKt: typeof raw.groundSpeedKt === "number" ? raw.groundSpeedKt : null,
    squawk: typeof raw.squawk === "number" ? raw.squawk : null,
    trackDeg: typeof raw.trackDeg === "number" ? raw.trackDeg : null,
    fuelTotalGal: typeof raw.fuelTotalGal === "number" && Number.isFinite(raw.fuelTotalGal) ? raw.fuelTotalGal : null,
    fuelUsedGal: typeof raw.fuelUsedGal === "number" && Number.isFinite(raw.fuelUsedGal) ? raw.fuelUsedGal : null,
    fuelStartGal: typeof raw.fuelStartGal === "number" && Number.isFinite(raw.fuelStartGal) ? raw.fuelStartGal : null,
    ...(typeof raw.fuelQtyLb === "number" && Number.isFinite(raw.fuelQtyLb) ? { fuelQtyLb: raw.fuelQtyLb } : {}),
    ...(typeof raw.gearDown === "boolean" ? { gearDown: raw.gearDown } : {}),
    source,
    stale: raw.stale !== false,
  };
}

function fixNum(n: number | null | undefined): string {
  return n == null || !Number.isFinite(n) ? "" : n.toFixed(5);
}

/**
 * Same aircraft fix — clock ignored. `sampleFromSi` stamps `new Date()` on
 * every call, so comparing `atUtc` would rewrite zustand every App render.
 */
function fuelQtyEquals(a?: number | null, b?: number | null): boolean {
  const left = a != null && Number.isFinite(a) ? Math.round(a * 10) / 10 : null;
  const right = b != null && Number.isFinite(b) ? Math.round(b * 10) / 10 : null;
  return left === right;
}

export function fusedFixEquals(a?: FusedPosition | null, b?: FusedPosition | null): boolean {
  if (!a || !b) return a === b;
  return (
    a.source === b.source &&
    a.stale === b.stale &&
    fixNum(a.lat) === fixNum(b.lat) &&
    fixNum(a.lon) === fixNum(b.lon) &&
    fixNum(a.heading) === fixNum(b.heading) &&
    fixNum(a.altitudeFt) === fixNum(b.altitudeFt) &&
    (a.onGround ?? null) === (b.onGround ?? null) &&
    fuelQtyEquals(a.fuelTotalGal, b.fuelTotalGal) &&
    fuelQtyEquals(a.fuelUsedGal, b.fuelUsedGal) &&
    fuelQtyEquals(a.fuelStartGal, b.fuelStartGal) &&
    fuelQtyEquals(a.fuelQtyLb, b.fuelQtyLb) &&
    (a.gearDown ?? null) === (b.gearDown ?? null)
  );
}

/**
 * Identity of a live fix for the desk publish path.
 * Lat/lon alone would hide a fuel or gear change while the aircraft is stopped.
 * Fuel is bucketed by 10 lb so a one-pound tick does not republish.
 */
export function liveFixToken(fused: Pick<FusedPosition, "source" | "lat" | "lon" | "fuelQtyLb" | "gearDown" | "onGround">): string {
  const fuel = fused.fuelQtyLb != null && Number.isFinite(fused.fuelQtyLb) ? Math.round(fused.fuelQtyLb / 10) : "";
  const gear = fused.gearDown === true ? "1" : fused.gearDown === false ? "0" : "";
  const ground = fused.onGround === true ? "1" : fused.onGround === false ? "0" : "";
  return `${fused.source}|${fused.lat.toFixed(4)}|${fused.lon.toFixed(4)}|${fuel}|${gear}|${ground}`;
}

/** Live SimConnect / SI sample worth persisting. Last-known and unchanged fixes are skipped. */
export function liveFixToPersist(
  fused?: FusedPosition | null,
  lastKnown?: FusedPosition | null,
): FusedPosition | null {
  if (!fused || fused.source === "last_known") return null;
  const persist = hydrateFusedPosition(fused);
  if (!persist || persist.source === "last_known") return null;
  if (fusedFixEquals(lastKnown, persist)) return null;
  return persist;
}

/**
 * Zustand persist stringifies the whole career on every `set()`.
 * Moving SimConnect must not rewrite last-known every second.
 */
export function shouldStoreLastKnown(
  prev: FusedPosition | null | undefined,
  next: FusedPosition,
): boolean {
  if (!prev) return true;
  if (prev.source !== next.source || Boolean(prev.stale) !== Boolean(next.stale)) return true;
  if ((prev.onGround ?? null) !== (next.onGround ?? null)) return true;
  const dt = Date.parse(next.atUtc) - Date.parse(prev.atUtc);
  if (Number.isFinite(dt) && dt >= LAST_KNOWN_STORE_MIN_MS) return true;
  return haversineNm(prev.lat, prev.lon, next.lat, next.lon) >= LAST_KNOWN_STORE_MIN_NM;
}

export function positionLabel(pos: FusedPosition | null): string {
  if (!pos) return "POS —";
  if (pos.source === "simconnect") return pos.stale ? "POS SIM (stale)" : "POS SIM";
  if (pos.source === "sayintentions") return pos.stale ? "POS SI (stale)" : "POS SI";
  if (pos.source === "vatsim") return pos.stale ? "POS VATSIM (stale)" : "POS VATSIM";
  return "POS LAST";
}

export type NearbyAirport = {
  airport: SceneryAirport;
  distanceNm: number;
};

export function nearbyDestinations(
  pos: Pick<PositionSample, "lat" | "lon">,
  scenery: SceneryAirport[],
  maxNm = 120,
  limit = 8,
): NearbyAirport[] {
  const rows: NearbyAirport[] = [];
  for (const raw of scenery) {
    const apt = hydrateSceneryAirport(raw.icao, scenery);
    if (!apt) continue;
    const c = resolveCoords(apt.icao, scenery);
    if (!c) continue;
    const distanceNm = haversineNm(pos.lat, pos.lon, c.lat, c.lon);
    if (distanceNm <= maxNm) rows.push({ airport: apt, distanceNm });
  }
  rows.sort((a, b) => a.distanceNm - b.distanceNm);
  return rows.slice(0, limit);
}

export function nearestDestination(
  pos: Pick<PositionSample, "lat" | "lon">,
  scenery: SceneryAirport[],
  maxNm = 180,
): NearbyAirport | null {
  return nearbyDestinations(pos, scenery, maxNm, 1)[0] ?? null;
}

export type FetchSimConnectResult =
  | { ok: true; sample: PositionSample; transport: SimConnectTransport }
  | { ok: false; error: string };

export type FetchSimConnectOptions = {
  fetchImpl?: typeof fetch;
  now?: Date;
  /** In-process Tauri/native poll. Tried first when provided. */
  nativePoll?: () => Promise<FetchSimConnectResult>;
  /** Abort a silent HTTP bridge. Default 2s so a dead localhost does not stall the next poll. */
  timeoutMs?: number;
};

export function joinSimConnectErrors(native: string, http: string, url = DEFAULT_SIMCONNECT_URL): string {
  const n = native.trim();
  const h = http.trim();
  if (n && h) return `${n} HTTP fallback (${url}): ${h}`;
  return n || h || "SimConnect unreachable.";
}

export function simConnectStatusLine(input: {
  enabled: boolean;
  error?: string;
  transport?: SimConnectTransport | "";
}): string {
  if (!input.enabled) {
    return "SimConnect position is off. Map uses SI (if any) then last-known.";
  }
  if (input.transport === "native" && !input.error) {
    return "Connected to MSFS SimConnect on this PC.";
  }
  if (input.transport === "http" && !input.error) {
    return "Connected via the HTTP SimConnect bridge.";
  }
  const err = input.error?.trim() ?? "";
  if (err) return err;
  return "Waiting for a SimConnect sample from MSFS on this PC…";
}

export async function fetchSimConnectSample(
  settings: SimConnectSettings = DEFAULT_SIMCONNECT,
  opts: FetchSimConnectOptions = {},
): Promise<FetchSimConnectResult> {
  if (!settings.enabled) return { ok: false, error: "SimConnect poll is off." };
  const now = opts.now ?? new Date();
  const fetchImpl = opts.fetchImpl ?? fetch;
  const url = simConnectUrl(settings);

  let nativeError = "";
  if (opts.nativePoll) {
    try {
      const native = await opts.nativePoll();
      if (native.ok) {
        return { ok: true, sample: native.sample, transport: native.transport ?? "native" };
      }
      nativeError = native.error;
    } catch (err) {
      nativeError = err instanceof Error ? err.message : "Native SimConnect failed.";
    }
  }

  const timeoutMs = Math.max(50, opts.timeoutMs ?? 2000);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!res.ok) {
      return { ok: false, error: joinSimConnectErrors(nativeError, `SimConnect HTTP ${res.status}`, url) };
    }
    const raw = (await res.json()) as unknown;
    const sample = parsePositionSample(raw, now);
    if (!sample) {
      return { ok: false, error: joinSimConnectErrors(nativeError, "SimConnect sample had no lat/lon.", url) };
    }
    return { ok: true, sample, transport: "http" };
  } catch (err) {
    const aborted = controller.signal.aborted;
    const msg = aborted
      ? `SimConnect HTTP timed out after ${timeoutMs}ms.`
      : err instanceof Error
        ? err.message
        : "SimConnect unreachable.";
    return { ok: false, error: joinSimConnectErrors(nativeError, msg, url) };
  } finally {
    clearTimeout(timer);
  }
}
